import type { IBlackbaudRaisersEdgeIntegrationAttributes } from '@feathr/blackbox';
import type { IIntegrationUIToggleSection, TIntegrationUIToggleValues } from '@feathr/components';

import { DONATION_UPSYNC_KEY, GIFT_DOWNSYNC_KEY } from '../raisersEdgeConfig';

type TDownsyncActivities = IBlackbaudRaisersEdgeIntegrationAttributes['downsync_activities'];
type TUpsyncActivities = IBlackbaudRaisersEdgeIntegrationAttributes['upsync_activities'];

/** The activity toggles' values from the model. */
export function getActivityValues({
  downsync_activities: downsync,
  upsync_activities: upsync,
}: Partial<
  Pick<IBlackbaudRaisersEdgeIntegrationAttributes, 'downsync_activities' | 'upsync_activities'>
>): TIntegrationUIToggleValues {
  return {
    [DONATION_UPSYNC_KEY]: upsync?.donations ?? false,
    [GIFT_DOWNSYNC_KEY]: downsync?.gift_events ?? false,
  };
}

/** The downsync activities with gift downsync set. Keeps the other activities as they are. */
export function setGiftDownsync(
  downsync: TDownsyncActivities | undefined,
  value: boolean,
): TDownsyncActivities {
  return { constituents: downsync?.constituents ?? false, ...downsync, gift_events: value };
}

/** The upsync activities with donation upsync set. Keeps the other activities as they are. */
export function setDonationUpsync(
  upsync: TUpsyncActivities | undefined,
  value: boolean,
): TUpsyncActivities {
  return { ...upsync, donations: value };
}

/** Whether donation upsync is on with no fund to put the donations in. */
export function isFundMissing({
  fundraising,
  upsync_activities: upsync,
}: Partial<
  Pick<IBlackbaudRaisersEdgeIntegrationAttributes, 'fundraising' | 'upsync_activities'>
>): boolean {
  return !!upsync?.donations && !fundraising?.fund_id;
}

/** The sections with one toggle disabled for `reason`. */
export function disableToggle(
  sections: readonly IIntegrationUIToggleSection[],
  key: string,
  reason: string,
): IIntegrationUIToggleSection[] {
  return sections.map((section) => ({
    ...section,
    toggles: section.toggles.map((toggle) =>
      toggle.key === key ? { ...toggle, disabledReason: reason } : toggle,
    ),
  }));
}
